//#region [IMPORT]

// === THIRD-PARTY ===
import React, { createContext, useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';

// ### MUI / STYLES ###
import {
  Box,
  Table,
  TableBody,
  TableContainer,
  TableHead,
  TableRow,
  TableCell,
  Snackbar,
} from '@mui/material';
import { Cancel, CheckCircle, UnfoldMoreOutlined } from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';
import MuiAlert from '@mui/material/Alert';

// === UTILS ===
import { getHexAlpha } from 'components/styles.utils';

// === API ===
import { on, removeListener, requestFileInfo, requestFile, requestUsers, assignFile } from 'api/prokey/prokey.websocket';

// === COMPONENTS ===
import {ControlBox} from './components/ControlBox/index.jsx';
import {AppContext} from 'pages/App/components/AppContext/AppContext.jsx';

//#endregion

//-----------------------------------------------------------------
function Alert(props) {
  //-----------------------------------------------------------------
  return <MuiAlert elevation={6} variant='filled' {...props} />;
}

const DOCUMENT_COMPARE_METHODS = {
  num_opened: (a, b) => {
    return (a || 0) - (b || 0);
  },
  num_closed: (a, b) => {
    return (a || 0) - (b || 0);
  },
  passed: (a, b) => {
    return (a || 0) - (b || 0);
  },
  package_count: (a, b) => {
    return (a || 0) - (b || 0);
  },
  assigned_user: (a, b) => {
    const cmp_a = (a || '').toUpperCase();
    const cmp_b = (b || '').toUpperCase();
    return cmp_a < cmp_b ? -1 : cmp_a > cmp_b ? 1 : 0;
  },
  loan_number: (a, b) => {
    const cmp_a = (a || '').toUpperCase();
    const cmp_b = (b || '').toUpperCase();
    return cmp_a < cmp_b ? -1 : cmp_a > cmp_b ? 1 : 0;
  },
};

//-----------------------------------------------------------------
export function FileList() {
  //-----------------------------------------------------------------

  //#region [VALUES]

  const { user_details } = useContext( AppContext );

  const theme    = useTheme();
  const navigate = useNavigate();

  const [ files    , setFiles    ] = useState( [] );
  const [ selected , setSelected ] = useState( {} );
  const [ sort_header , setSortHeader ] = useState( 'loan_number' );
  const [ sort_ascend , setSortAscend ] = useState( true );
  const [ search   , setSearch   ] = useState( null );
  const [ filters  , setFilters  ] = useState( [] );
  const [ files_filtered , setFilesFiltered ] = useState( [] );
  const [ is_open        , setIsOpen        ] = useState( true );

  const [ message, setMessage          ] = useState('');
  const [ show_message, setShowMessage ] = useState( false );
  
  //#endregion

  //#region [STYLES]

  const _styles = {
    task_queue_text: {
      fontSize: 'x-large',
      paddingLeft: '1rem',
    },
    table_header: {
      // boxShadow : '0 0 0.5em 0.01em' + theme.palette.primary.main,
    },
    table_header_cell: {
      backgroundColor: theme.palette.primary.main,// + getHexAlpha(100),
      color: theme.palette.primary.contrastText,
      textAlign: 'center',
    },
    table_container: {
      height    : '100%',
      width     : '100%',
      maxWidth  : '100%',
      overflowY : 'hidden',
    },
    table_container2: {
      display   : is_open ? '' : 'none',
      height    : '100%',
      width     : '100%',
      maxWidth  : '100%',
      padding   : '0',
      overflowY : 'auto',
    },
    table_row: {
      cursor: 'pointer',
      '&:hover': {
        backgroundColor: theme.palette.primary.main + getHexAlpha(20),
      },
      '&.Mui-selected': {
        backgroundColor: theme.palette.primary.main + getHexAlpha(20),
        '&:hover': {
          backgroundColor: theme.palette.primary.main + getHexAlpha(30),
        },
      },
    },
    table_cell: {
      // textAlign: 'center',
      // justifyContent: 'center',
      borderBottom: "none",
    },
    card: {
      display      : 'grid',
      gap          : '0.5rem',
      'gridTemplateColumns': 'repeat(  1, 1fr )',
      'gridTemplateRows'   : 'max-content 1fr',
      height       : 'calc( 100% - 2rem )' ,
      width        : 'calc( 100% - 2rem )' ,
      maxHeight    : '100%'   ,
      maxWidth     : '100%'   ,
      overflowY    : 'hidden' ,
      marginTop    : '1rem'   ,
      marginLeft   : '1rem'   ,
      marginRight  : '1rem'   ,
      border       : '1px solid ' + theme.palette.background.primary[theme.palette.mode],
      borderRadius : '1rem'   ,
      backgroundColor: theme.palette.background.primary[theme.palette.mode],
    },
    table_header_passed: {
      width: '5rem',
      cursor: 'pointer',
      backgroundColor: sort_header === 'passed' ? theme.palette.primary.dark : theme.palette.primary.main + getHexAlpha(100),
      color: theme.palette.primary.contrastText,
    },
    table_header_open: {
      width: '5rem',
      cursor: 'pointer',
      backgroundColor: sort_header === 'open' ? theme.palette.primary.dark : theme.palette.primary.main + getHexAlpha(100),
      color: theme.palette.primary.contrastText,
    },
    table_header_closed: {
      width: '5rem',
      cursor: 'pointer',
      backgroundColor: sort_header === 'closed' ? theme.palette.primary.dark : theme.palette.primary.main + getHexAlpha(100),
      color: theme.palette.primary.contrastText,
    },
    table_header_packages: {
      width: '5rem',
      cursor: 'pointer',
      backgroundColor: sort_header === 'packages' ? theme.palette.primary.dark : theme.palette.primary.main + getHexAlpha(100),
      color: theme.palette.primary.contrastText,
    },
    table_header_user: {
      cursor: 'pointer',
      backgroundColor: sort_header === 'user' ? theme.palette.primary.dark : theme.palette.primary.main + getHexAlpha(100),
      color: theme.palette.primary.contrastText,
    },
    table_header_name: {
      cursor: 'pointer',
      backgroundColor: sort_header === 'name' ? theme.palette.primary.dark : theme.palette.primary.main + getHexAlpha(100),
      color: theme.palette.primary.contrastText,
    },
  };

  const TABLE_HEADER_PASSED = {
    sx            : _styles.table_header_passed,
    sortDirection : sort_ascend ? 'asc' : 'desc',
    onClick       : () => onClickHeader( 'passed' ),
    children      : <Box sx={{display: 'flex'}}><UnfoldMoreOutlined sx={{alignItem: 'center' }} /> Passed</Box>,
    name          : 'da_sort_tbl_header_passed_btn',
  }

  const TABLE_HEADER_OPEN = {
    sx            : _styles.table_header_open,
    sortDirection : sort_ascend ? 'asc' : 'desc',
    onClick       : () => onClickHeader( 'num_opened' ),
    children      : <Box sx={{display: 'flex'}}><UnfoldMoreOutlined sx={{alignItem: 'center' }} /> Open</Box>,
    name          : 'da_sort_tbl_header_open_btn',
  }

  const TABLE_HEADER_CLOSED = {
    sx            : _styles.table_header_closed,
    sortDirection : sort_ascend ? 'asc' : 'desc',
    onClick       : () => onClickHeader( 'num_closed' ),
    children      : <Box sx={{display: 'flex'}}><UnfoldMoreOutlined sx={{alignItem: 'center' }} /> Closed</Box>,
    name          : 'da_sort_tbl_header_closed_btn',
  }

  const TABLE_HEADER_PACKAGES = {
    sx            : _styles.table_header_packages,
    sortDirection : sort_ascend ? 'asc' : 'desc',
    onClick       : () => onClickHeader( 'package_count' ),
    children      : <Box sx={{display: 'flex'}}><UnfoldMoreOutlined sx={{alignItem: 'center' }} /> Packages</Box>,
    name          : 'da_sort_tbl_header_packages_btn',
  }

  const TABLE_HEADER_USER = {
    sx            : _styles.table_header_user,
    sortDirection : sort_ascend ? 'asc' : 'desc',
    onClick       : () => onClickHeader( 'assigned_user' ),
    children      : <Box sx={{display: 'flex'}}><UnfoldMoreOutlined sx={{alignItem: 'center' }} /> Assigned User</Box>,
    name          : 'da_sort_tbl_header_assigned_user_btn',
  }

  const TABLE_HEADER_NAME = {
    sx            : _styles.table_header_name,
    sortDirection : sort_ascend ? 'asc' : 'desc',
    onClick       : () => onClickHeader( 'loan_number' ),
    children      : <Box sx={{display: 'flex'}}><UnfoldMoreOutlined sx={{alignItem: 'center' }} /> Name</Box>,
    name          : 'da_sort_tbl_header_name_btn',
  }
  //#endregion

  //#region [EFFECT]

  useEffect(() => {
    //-----------------------------------------------------------------
    function listener_file_info(data) {
      //-----------------------------------------------------------------
      if(Object.prototype.toString.call(data) !== '[object Array]') {
        data = Object.values( data );
      }
      setFiles(data);
    }

    on( 'FILE-INFO' , listener_file_info );

    requestFileInfo();

    return () => {
      removeListener( 'FILE-INFO' , listener_file_info );
    };
  }, []); // empty dependency = called only once at startup

  useEffect(() => {
    try {
      const has_filters  = filters.length > 0;
      let filtered_files = has_filters ? files.filter( file => filters.every( filter => filter(file) ) ) : files;
      if( search ) {
        filtered_files = filtered_files.filter( file => file.loan_number?.toUpperCase().includes( search ) );
      }
      filtered_files.sort((a, b) => DOCUMENT_COMPARE_METHODS[sort_header](a[sort_header], b[sort_header]));
      if( !sort_ascend ) {
        filtered_files.reverse();
      }
  
      setFilesFiltered( [...filtered_files] )
    } catch (error) {
      console.warn(error);
    }
  }, [files, sort_header, sort_ascend, filters, search]);

  //#endregion

  //#region [EVENTS]

  //-----------------------------------------------------------------
  function handleFilter( value ) {
    //-----------------------------------------------------------------
    try {
      setFilters( value );
    } catch (error) {
      console.warn(error);
    }
  }

  //-----------------------------------------------------------------
  function handleSearch( value ) {
    //-----------------------------------------------------------------
    try {
      setSearch( value );
    } catch (error) {
      console.warn(error);
    }
  }

  //-----------------------------------------------------------------
  function handleOpen( value ) {
    //-----------------------------------------------------------------
    try {
      setIsOpen( value );
    } catch (error) {
      console.warn(error);
    }
  }

  //-----------------------------------------------------------------
  function onClick(id) {
    //-----------------------------------------------------------------
    try {
      const new_selected = {...selected};
      const has_id = new_selected.hasOwnProperty(id);
      
      if(has_id) delete new_selected[id];
      else new_selected[id] = true;

      setSelected(new_selected);
    } catch (error) {
      console.warn(error);
    }
  }

  //-----------------------------------------------------------------
  function onDoubleClick(event) {
    //-----------------------------------------------------------------
    try {
      const file_id = event.currentTarget.id;
      const file = files_filtered.find( x => (x.file_id) == (file_id) );

      if( !file ) return;

      if( file.assigned_user !== user_details.Username ) {
        setMessage('To access this file, you must be the assigned user!');
        setShowMessage(true);
        return;
      }

      navigate(`/analytics/files/${file_id}`);

    } catch (error) {
      console.warn(error);
    }
  }

  //-----------------------------------------------------------------
  function onClickHeader( name ) {
    //-----------------------------------------------------------------
    if( sort_header === name ) {
      setSortAscend( !sort_ascend );
    } else {
      setSortAscend( true );
      setSortHeader( name );
    }
  }

  //-----------------------------------------------------------------
  function handleMessageClose(event, reason) {
    //-----------------------------------------------------------------
    if (reason === 'clickaway') {
      return;
    }
    setShowMessage(false);
  };

  //#endregion

  //#region [DISPLAY]

  //-----------------------------------------------------------------
  function displayMessage() {
    //-----------------------------------------------------------------
    return (
      <Snackbar open={show_message} autoHideDuration={4000} onClose={handleMessageClose}  anchorOrigin={{vertical: 'bottom', horizontal: 'center'}}>
        <div>
          <Alert onClose={handleMessageClose} severity='error'>
            {message}
          </Alert>
        </div>
      </Snackbar>
    );
  };
  //-----------------------------------------------------------------
  function display() {
    //-----------------------------------------------------------------
    const has_files = files_filtered.length > 0;
    return (
      <Box sx={_styles.card}>
        <ControlBox files={selected} onFilter={handleFilter} onSearch={handleSearch} onOpen={handleOpen} />
        {displayMessage()}
        <TableContainer sx={_styles.table_container2}>
          <Table stickyHeader size='small'>
            <TableHead sx={_styles.table_header}>
              <TableRow>
                <TableCell sx={_styles.table_header_cell} key={0}>{ '' }</TableCell>

                <TableCell key={1} { ...TABLE_HEADER_PASSED    } />
                <TableCell key={2} { ...TABLE_HEADER_NAME      } />
                <TableCell key={3} { ...TABLE_HEADER_OPEN      } />
                <TableCell key={4} { ...TABLE_HEADER_CLOSED    } />
                <TableCell key={5} { ...TABLE_HEADER_PACKAGES  } />
                <TableCell key={6} { ...TABLE_HEADER_USER      } />

                <TableCell sx={_styles.table_header_cell} key={7}>{ '' }</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>{ has_files ? displayFiles() : displayEmpty() }</TableBody>
          </Table>
        </TableContainer>
      </Box>
    );
  }

  //-----------------------------------------------------------------
  function displayFiles() {
    //-----------------------------------------------------------------
    const result = [];

    for( const file of Object.values(files_filtered) ) {
      const is_selected = selected.hasOwnProperty(file.file_id);

      result.push(
        <TableRow sx={_styles.table_row} key={file.file_id} id={file.file_id} 
          onClick={()=>{onClick(file.file_id)}}
          onDoubleClick={onDoubleClick}
          selected={is_selected}
        >
          <TableCell sx={_styles.table_cell} size='large' key={0}>  </TableCell>
          <TableCell sx={_styles.table_cell} size='large' key={1}>
            {file.review ? <div /> : file.passed ? <CheckCircle color='success'/> : <Cancel color='error'/>}
          </TableCell>
          <TableCell sx={_styles.table_cell} size='large' key={2}> { file.loan_number   } </TableCell>
          <TableCell sx={_styles.table_cell} size='large' key={3}> { file.num_opened    } </TableCell>
          <TableCell sx={_styles.table_cell} size='large' key={4}> { file.num_closed    } </TableCell>
          <TableCell sx={_styles.table_cell} size='large' key={5}> { file.package_count } </TableCell>
          <TableCell sx={_styles.table_cell} size='large' key={6}> { file.assigned_user } </TableCell>
          {/* <TableCell sx={_styles.table_cell} size='large' key={4}> { file.start_time    } </TableCell> */}
          <TableCell sx={_styles.table_cell} size='large' key={7}>  </TableCell>
        </TableRow>
      );
    }

    return result;
  }

  //-----------------------------------------------------------------
  function displayEmpty() {
    //-----------------------------------------------------------------
    return (
      <TableRow>
        <TableCell colSpan='7' className='allEmptyHere'>
          All empty here...
        </TableCell>
      </TableRow>
    );
  }

  //#endregion

  return display();
}